import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import { useUsers } from '../../hooks/useUsers';
import { GET_CONVERSATION_MEMBERS } from '../../graphql/operations';
import authService from '../../auth/authService';
import Badge from '../shared/Badge';
import ManageMembersModal from './ManageMembersModal';
import './ChatHeader.css';

function derivePresenceTone(user) {
  if (!user) return 'offline';
  if (user.presenceOverride === 'offline') return 'offline';
  if (!user.lastSeenAt) return 'offline';
  const lastSeenMs = new Date(user.lastSeenAt).getTime();
  if (!Number.isFinite(lastSeenMs)) return 'offline';
  const diffMinutes = Math.floor((Date.now() - lastSeenMs) / 60000);
  return diffMinutes < 2 ? 'online' : 'offline';
}

function ChatHeader({ conversation }) {
  const { users, userMap } = useUsers();
  const { toggleAI, aiToggleEnabled } = useContext(WorkspaceContext);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [aiAnnouncement, setAiAnnouncement] = useState('');
  const previousAIStateRef = useRef({ id: null, aiEnabled: null });

  const title = (conversation.title || '').trim();
  const viewerTitle = (conversation.viewerTitle || '').trim();
  const viewerCounterpartId = (conversation.viewerCounterpartId || '').trim();
  const type = conversation.type || (title && !title.includes('@') ? 'CHANNEL' : 'DM');
  const memberCount = conversation.memberCount || (conversation.members || []).length;
  const isGroupDm = type === 'DM' && memberCount > 2;
  const currentUserId = (authService.getEmail() || '').toLowerCase();
  const description = conversation.description || '';
  const showAIToggle = aiToggleEnabled && (type === 'CHANNEL' || type === 'DM');

  // Resolve display title: prefer viewer-specific title (correct for both
  // participants of a DM), then fall back to email→displayName lookup, then
  // raw title. This is the fix for the "DM shows your own name" bug — the
  // stored `title` is the creator's perspective and unreliable for the peer.
  const displayTitle = useMemo(() => {
    if (viewerTitle) return viewerTitle;
    if (!title) return 'Untitled Conversation';
    if (!title.includes('@')) return title;
    const user = users.find((u) => u.username === title);
    return user?.displayName || title;
  }, [viewerTitle, title, users]);

  // For DMs, look up presence info via the viewer-resolved counterpart id —
  // clean and bug-free. Fall back to title-keyed lookup only when the
  // backend hasn't populated viewerCounterpartId yet.
  const dmUser = useMemo(() => {
    if (type !== 'DM') return null;
    if (viewerCounterpartId && userMap[viewerCounterpartId]) {
      return userMap[viewerCounterpartId];
    }
    return userMap[title] || null;
  }, [type, viewerCounterpartId, title, userMap]);
  const dmPresenceTone = derivePresenceTone(dmUser);

  const { data: membersData } = useQuery(GET_CONVERSATION_MEMBERS, {
    variables: { conversationId: conversation.id },
    skip: !isGroupDm,
    fetchPolicy: 'cache-and-network'
  });

  const groupPillMembers = useMemo(() => {
    if (!isGroupDm) return [];
    const members = membersData?.getConversationMembers || [];
    const others = members.filter((member) => String(member.userId || '').toLowerCase() !== currentUserId);
    return others.slice(0, 2).map((member) => {
      const user = userMap[member.userId] || null;
      const displayName = user?.displayName || member.displayName || member.userId;
      return {
        userId: member.userId,
        displayName,
        initial: String(displayName || member.userId || '?').charAt(0).toUpperCase(),
        presenceTone: derivePresenceTone(user)
      };
    });
  }, [currentUserId, isGroupDm, membersData?.getConversationMembers, userMap]);

  const groupPillAriaLabel = useMemo(() => {
    if (!isGroupDm) return '';
    const names = groupPillMembers.map((member) => member.displayName).join(', ');
    const suffix = memberCount > groupPillMembers.length ? ` +${memberCount - groupPillMembers.length} more` : '';
    const summary = names ? `${names}${suffix}` : `${memberCount} members`;
    return `View members: ${summary}`;
  }, [groupPillMembers, isGroupDm, memberCount]);

  useEffect(() => {
    if (!showAIToggle || !conversation?.id) return;
    const previous = previousAIStateRef.current;
    if (
      previous.id === conversation.id &&
      previous.aiEnabled !== null &&
      previous.aiEnabled !== conversation.aiEnabled
    ) {
      setAiAnnouncement(
        `AI assistance ${conversation.aiEnabled ? 'enabled' : 'disabled'} for ${displayTitle}.`
      );
    }
    previousAIStateRef.current = {
      id: conversation.id,
      aiEnabled: !!conversation.aiEnabled
    };
  }, [conversation?.id, conversation?.aiEnabled, displayTitle, showAIToggle]);

  const handleToggleAI = () => {
    if (!conversation?.id) return;
    if (conversation.aiEnabled) {
      if (!window.confirm('Disable AI assistance for this conversation?')) {
        return;
      }
    }
    toggleAI(conversation.id);
  };

  return (
    <>
      <div className="chat-panel-header">
        <div className="chat-panel-header-left">
          {type === 'CHANNEL' && <span className="chat-panel-hash">#</span>}
          {type === 'DM' && !isGroupDm && dmUser && (
            <span className={`chat-panel-presence-dot chat-panel-presence-${dmPresenceTone}`} />
          )}
          {type === 'AI' && (
            <span className="chat-panel-presence-dot chat-panel-presence-ai" />
          )}
          <h2 className="chat-panel-title">{displayTitle}</h2>
          <Badge type={type} />
          {type === 'DM' && dmUser?.statusMessage && (
            <span className="chat-panel-status-message">{dmUser.statusMessage}</span>
          )}
        </div>
        <div className="chat-panel-header-right">
          {showAIToggle && (
            <>
              <label className="chat-panel-ai-switch">
                <input
                  type="checkbox"
                  checked={!!conversation.aiEnabled}
                  onChange={handleToggleAI}
                  aria-label={`AI assistance ${conversation.aiEnabled ? 'enabled' : 'disabled'} for ${displayTitle}`}
                />
                <span className="chat-panel-ai-track">
                  <span className="chat-panel-ai-thumb" />
                </span>
                <span className="chat-panel-ai-label">AI {conversation.aiEnabled ? 'On' : 'Off'}</span>
              </label>
              <span className="chat-panel-header-divider" aria-hidden="true" />
            </>
          )}
          {description && type === 'CHANNEL' && (
            <span className="chat-panel-description">{description}</span>
          )}
          {isGroupDm && (
            <button
              type="button"
              className="chat-panel-group-pill"
              onClick={() => setShowManageMembers(true)}
              aria-label={groupPillAriaLabel}
              title={displayTitle}
            >
              <span className="chat-panel-group-avatars" aria-hidden="true">
                {groupPillMembers.map((member, index) => (
                  <span
                    key={member.userId || `${member.displayName}-${index}`}
                    className={`chat-panel-group-avatar avatar-index-${index}`}
                  >
                    {member.initial}
                    <span className={`chat-panel-group-avatar-presence ${member.presenceTone}`} />
                  </span>
                ))}
              </span>
              <span className="chat-panel-group-count">{memberCount}</span>
            </button>
          )}
          {memberCount > 0 && (
            <span className="chat-panel-members">
              {memberCount} member{memberCount !== 1 ? 's' : ''}
            </span>
          )}
          {type === 'CHANNEL' && (
            <button
              type="button"
              className="chat-panel-edit-members"
              onClick={() => setShowManageMembers(true)}
            >
              Members
            </button>
          )}
        </div>
      </div>
      <span className="sr-only" aria-live="polite">
        {aiAnnouncement}
      </span>

      {showManageMembers && (
        <ManageMembersModal
          conversation={conversation}
          readOnly={isGroupDm}
          titleOverride={isGroupDm ? `Members — ${displayTitle}` : ''}
          onClose={() => setShowManageMembers(false)}
        />
      )}
    </>
  );
}

export default ChatHeader;
